import React from "react";
import { useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import Spinner from "@/components/ui/spinner";
import { useUsers } from "@/entities/users/hooks";
import { INTEGRATIONS_TRANSLATION_PREFIX } from "@/pages/settings/components/integrations/integrations";

const PREFIX = `${INTEGRATIONS_TRANSLATION_PREFIX}.items.pandadoc.reps`;

const RepList: React.FC<{ userIds: string[]; dataTest: string }> = ({
  userIds,
  dataTest
}) => {
  const { users, isLoading } = useUsers(userIds);
  // A user not loaded yet reads "Unknown", so the names wait for the list.
  if (isLoading) {
    return (
      <div data-test={`${dataTest}-loading`}>
        <Spinner size="sm" />
      </div>
    );
  }
  return (
    <ul className="flex flex-col gap-1" data-test={dataTest}>
      {/* Keyed by the requested id: every user not loaded yet shares one placeholder id. */}
      {users.map((user, index) => (
        <li key={userIds[index]}>
          <Text variant="body">{user.displayName || user.email}</Text>
        </li>
      ))}
    </ul>
  );
};

// Which reps PandaDoc knows by email: only they can send proposals.
export const PandaDocReps: React.FC<{
  matchedUserIds: string[];
  unmatchedUserIds: string[];
}> = ({ matchedUserIds, unmatchedUserIds }) => {
  const { t } = useTranslation();
  return (
    <div
      className="flex w-full flex-col gap-4 rounded-2xl border-[0.5px] border-border-widget-card bg-surface-widget-card p-4"
      data-test="pandadoc-reps"
    >
      <div className="flex flex-col gap-2">
        <Text variant="lgBody" className="font-medium">
          {t(`${PREFIX}.matchedTitle`, { count: matchedUserIds.length })}
        </Text>
        {matchedUserIds.length > 0 ? (
          <RepList userIds={matchedUserIds} dataTest="pandadoc-reps-matched" />
        ) : (
          <Text variant="bodySecondary">{t(`${PREFIX}.noneMatched`)}</Text>
        )}
      </div>
      {unmatchedUserIds.length > 0 && (
        <div className="flex flex-col gap-2">
          <Text variant="lgBody" className="font-medium">
            {t(`${PREFIX}.unmatchedTitle`, {
              count: unmatchedUserIds.length
            })}
          </Text>
          <Text variant="bodySecondary">{t(`${PREFIX}.unmatchedHelp`)}</Text>
          <RepList
            userIds={unmatchedUserIds}
            dataTest="pandadoc-reps-unmatched"
          />
        </div>
      )}
    </div>
  );
};
